import {
  AccountRoster,
  Button,
  Dialog,
  ErrorText,
  revealDdAccountNumber,
  type AccountRosterActions,
  type DdAccountKind,
  type ListDdAccountsResponse,
} from '@dataroom/shared';
import { useCreateDdAccount, useDdAccounts, useUpdateDdAccount } from '@/api/hooks';

/** The kinds to show: one roster question's kind, or the kind whose placeholder
 * a checklist item sits under; neither (or no match) shows every released kind. */
function kindsFor(
  data: ListDdAccountsResponse,
  kind: DdAccountKind | null | undefined,
  placeholderTagId: string | null | undefined,
): DdAccountKind[] | undefined {
  if (kind) return [kind];
  if (placeholderTagId) {
    const match = data.kinds.filter((k) => k.placeholders.some((p) => p.tagId === placeholderTagId));
    if (match.length > 0) return match.map((k) => k.kind);
  }
  return undefined;
}

/**
 * The seller's list of the company's accounts (ADR-0037): add and correct
 * accounts, and see a stored full number only by asking for it. Each account
 * gets its own checklist category for its statements. No merge — the deal team
 * does that.
 */
export function DdAccountsPanel({
  roomId,
  kind,
  placeholderTagId,
}: {
  roomId: string;
  kind?: DdAccountKind | null;
  placeholderTagId?: string | null;
}) {
  const accounts = useDdAccounts(roomId);
  const create = useCreateDdAccount(roomId);
  const update = useUpdateDdAccount(roomId);

  if (accounts.isPending) return <p className="text-sm text-muted-foreground">Loading accounts…</p>;
  if (accounts.error) return <ErrorText error={accounts.error} />;

  const actions: AccountRosterActions = {
    create: (body) => create.mutateAsync(body),
    update: (accountId, body) => update.mutateAsync({ accountId, body }),
    reveal: (accountId) => revealDdAccountNumber(roomId, accountId),
  };
  return (
    <AccountRoster
      data={accounts.data}
      kinds={kindsFor(accounts.data, kind, placeholderTagId)}
      actions={actions}
    />
  );
}

/** The account list in a dialog, opened from a "one category per account"
 * checklist item. */
export function DdAccountsDialog({
  roomId,
  placeholderTagId,
  onClose,
}: {
  roomId: string;
  placeholderTagId?: string | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      title="Your accounts"
      description="List each account once. Each gets its own place on the checklist for its statements. Only the last 4 digits of a number are shown."
      size="lg"
      onClose={onClose}
      footer={
        <Button variant="outline" className="ml-auto" onClick={onClose}>
          Done
        </Button>
      }
    >
      <DdAccountsPanel roomId={roomId} placeholderTagId={placeholderTagId} />
    </Dialog>
  );
}
